/* eslint-disable class-methods-use-this */
import { ACSCache } from './ACSCache';
import { ACS_CONFIG } from './ACSConfig';
import { ACSKey, SerializedACSContainer } from './ACSTypes';

const DB_NAME = 'cnu';
const DB_VERSION = ACS_CONFIG.version;
const ACS_STORE_NAME = 'active-contracts';

export class ACSCacheIndexedDB extends ACSCache {
  public static canUse(): boolean {
    return typeof window !== 'undefined' && 'indexedDB' in window;
  }

  public async set(serializedContainer: SerializedACSContainer): Promise<void> {
    const db = await this.openDB();

    return new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(ACS_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(ACS_STORE_NAME);

      const request = store.put({
        ...serializedContainer,
      });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  public async get(key: ACSKey): Promise<SerializedACSContainer | null> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(ACS_STORE_NAME, 'readonly');
      const store = transaction.objectStore(ACS_STORE_NAME);

      const request = store.get(key);

      request.onsuccess = () => {
        // We could add additional type-checking e.g. via ZOD, but it may impact performance as
        // everytime we request from IndexedDB we would need to validate the whole object.
        resolve((request.result as SerializedACSContainer) ?? null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  public async getAllKeys(): Promise<ACSKey[]> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(ACS_STORE_NAME, 'readonly');
      const store = transaction.objectStore(ACS_STORE_NAME);

      const request = store.getAllKeys();

      request.onsuccess = () => {
        resolve(request.result as ACSKey[]);
      };
      request.onerror = () => reject(request.error);
    });
  }

  public async has(key: ACSKey): Promise<boolean> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(ACS_STORE_NAME, 'readonly');
      const store = transaction.objectStore(ACS_STORE_NAME);

      const request = store.getKey(key);

      request.onsuccess = () => {
        resolve(!!request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  public async remove(key: ACSKey): Promise<void> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(ACS_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(ACS_STORE_NAME);

      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  public async clear(): Promise<void> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(ACS_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(ACS_STORE_NAME);

      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (db.objectStoreNames.contains(ACS_STORE_NAME)) {
          db.deleteObjectStore(ACS_STORE_NAME);
        }

        db.createObjectStore(ACS_STORE_NAME, {
          keyPath: 'key',
        });
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
